import { useCallback } from 'react';
import { useReactiveVar } from '@apollo/client';

import {
  INotificationsState,
  notificationsState,
  TNotificationsFilter,
  TNotificationsPane,
} from 'core/state';

const HOUR_MS = 60 * 60 * 1000;

export interface IUseNotificationCenter extends INotificationsState {
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  setPane: (pane: TNotificationsPane) => void;
  setFilter: (filter: TNotificationsFilter) => void;
  markAudioUnlocked: () => void;
  snoozeForAnHour: () => void;
  clearSnooze: () => void;
}

/**
 * Читает `notificationsState` и отдаёт его мутации (ТЗ 0068). Идиома та же,
 * что у `usePalette`: развернуть текущее значение и переприсвоить.
 */
export const useNotificationCenter = (): IUseNotificationCenter => {
  const state = useReactiveVar(notificationsState);

  const openPanel = useCallback(() => {
    notificationsState({ ...notificationsState(), open: true, pane: 'list' });
  }, []);

  const closePanel = useCallback(() => {
    notificationsState({ ...notificationsState(), open: false });
  }, []);

  const togglePanel = useCallback(() => {
    const current = notificationsState();
    notificationsState({
      ...current,
      open: !current.open,
      // Закрыли — возвращаемся к списку: открывать колокольчик и попадать в
      // настройки, которые правил вчера, человек не ожидает.
      pane: current.open ? 'list' : current.pane,
    });
  }, []);

  const setPane = useCallback((pane: TNotificationsPane) => {
    notificationsState({ ...notificationsState(), pane });
  }, []);

  const setFilter = useCallback((filter: TNotificationsFilter) => {
    notificationsState({ ...notificationsState(), filter });
  }, []);

  const markAudioUnlocked = useCallback(() => {
    if (notificationsState().audioUnlocked) return;
    notificationsState({ ...notificationsState(), audioUnlocked: true });
  }, []);

  const snoozeForAnHour = useCallback(() => {
    notificationsState({
      ...notificationsState(),
      mutedUntil: Date.now() + HOUR_MS,
    });
  }, []);

  const clearSnooze = useCallback(() => {
    notificationsState({ ...notificationsState(), mutedUntil: 0 });
  }, []);

  return {
    ...state,
    openPanel,
    closePanel,
    togglePanel,
    setPane,
    setFilter,
    markAudioUnlocked,
    snoozeForAnHour,
    clearSnooze,
  };
};
